import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce, useLoader } from "@shared/hooks";
import { ActionTypes, Option } from "@shared/interfaces";
import { useDispatch, useSelector } from "react-redux";
import { SelectPicker } from "rsuite";
import { getUniqueElements } from "@shared/utils";

import { Label } from "../Label";

interface AsyncSelectPickerInputProps<T> {
  labelTitle?: string;
  labelClassName?: string;
  value: Option | null;
  name: string;
  placeholder: string;
  className: string;
  disabled?: boolean;
  placement?: "auto";
  defaultOptions?: Option[];

  getData: (search: string) => void;
  selectData: any;
  prepareOptionFunction: (item: T) => Option;
  onChange: (value: Option | null) => void;

  loadingAction: ActionTypes;
  selectFirst?: boolean;
  notCleanable?: boolean;
  disableInitRequest?: boolean;
}

const AsyncSelectPickerInput = <T,>(props: AsyncSelectPickerInputProps<T>) => {
  const {
    labelTitle,
    labelClassName,
    getData,
    selectData,
    prepareOptionFunction,
    name,
    onChange,
    placeholder,
    className,
    loadingAction,
    disabled,
    placement,
    selectFirst,
    notCleanable,
    disableInitRequest,
    defaultOptions = [],
  } = props;

  const dispatch = useDispatch();

  const [inputValue, setInputValue] = useState<string>("");
  const [initiated, setInitiated] = useState(false);
  const debounceValue = useDebounce<string>(inputValue, 500);

  const items: Array<T> = useSelector(selectData());

  const options: Option[] = useMemo(() => {
    if (!props.value) return [...defaultOptions, ...items.map((i) => prepareOptionFunction(i))];

    const uniqueItems = getUniqueElements(
      [...defaultOptions, props.value, ...items.map((i) => prepareOptionFunction(i))],
      "value",
    );
    return uniqueItems.filter((i) => i.value);
  }, [items, props.value, prepareOptionFunction, defaultOptions]);

  const preparedValue: string = useMemo(() => {
    return props.value?.value ?? "";
  }, [props.value]);

  const { isLoading } = useLoader({
    name,
    actionTypes: useMemo(() => [loadingAction], [loadingAction]),
  });

  const handleChange = useCallback(
    (value: string) => {
      const selectedValue = options.find((option) => option.value === value) ?? null;
      onChange(selectedValue);
    },
    [options, onChange],
  );

  const onCloseMenu = useCallback(() => {
    if (debounceValue) {
      setInputValue("");
    }
  }, [debounceValue]);

  const renderMenu = useCallback(
    (menu: React.ReactNode) => {
      if (isLoading) {
        return <p style={{ padding: "6px 12px 12px", color: "#999" }}>Loading...</p>;
      }

      return menu;
    },
    [isLoading],
  );

  useEffect(() => {
    dispatch(getData(debounceValue));
  }, [dispatch, getData, debounceValue, disableInitRequest]);

  useEffect(() => {
    if (selectFirst && !initiated && options.length) {
      handleChange(options[0].value);
      setInitiated(true);
    }
  }, [selectFirst, initiated, handleChange, options]);

  return (
    <>
      {labelTitle ? <Label title={labelTitle} className={labelClassName} /> : null}
      <SelectPicker
        disabled={disabled}
        searchBy={() => true}
        data={options}
        value={preparedValue}
        onSearch={setInputValue}
        name={name}
        onChange={(value) => handleChange(value as string)}
        placeholder={placeholder}
        className={className}
        onClose={onCloseMenu}
        renderMenu={renderMenu}
        placement={placement}
        cleanable={!notCleanable}
      />
    </>
  );
};

export default AsyncSelectPickerInput;
